import { createContext, useContext, useEffect, useState } from "react";
import { api, setAuth, clearAuth, getStoredUser } from "@/lib/api";

const AuthContext = createContext(null);
const ACTIVE_CLIENT_KEY = "hbt_active_client";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getStoredUser());
  const [loading, setLoading] = useState(true);
  const [activeClientId, setActiveClientIdState] = useState(() => localStorage.getItem(ACTIVE_CLIENT_KEY) || "");

  const setActiveClientId = (id) => {
    setActiveClientIdState(id || "");
    if (id) localStorage.setItem(ACTIVE_CLIENT_KEY, id);
    else localStorage.removeItem(ACTIVE_CLIENT_KEY);
  };

  const userClientIds = (u) => {
    const list = [...(u?.client_ids || [])];
    if (u?.client_id && !list.includes(u.client_id)) list.unshift(u.client_id);
    return list;
  };

  const hasCap = (cap) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (user.role === "staff") return Array.isArray(user.capabilities) && user.capabilities.includes(cap);
    return false;
  };

  useEffect(() => {
    const t = localStorage.getItem("hbt_token");
    if (!t) {
      setLoading(false);
      return;
    }
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        localStorage.setItem("hbt_user", JSON.stringify(r.data));
        // if active_client isn't a member anymore, clear it
        const ids = userClientIds(r.data);
        if (activeClientId && ids.length && !ids.includes(activeClientId)) {
          setActiveClientId("");
        }
      })
      .catch(() => clearAuth())
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setAuth(data.token, data.refresh_token, data.user);
    setUser(data.user);
    // Reset any stale active-client scope on new login
    setActiveClientId("");
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    return data.user;
  };

  const logout = async () => {
    const refresh_token = localStorage.getItem("hbt_refresh");
    if (refresh_token) {
      try {
        await api.post("/auth/logout", { refresh_token });
      } catch (err) {
        console.warn("Logout revoke failed (token may already be expired)", err?.message);
      }
    }
    clearAuth();
    setActiveClientId("");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout,
      activeClientId, setActiveClientId,
      userClientIds: userClientIds(user),
      hasCap,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
